import { signal } from '@preact/signals'
import { useLocation } from 'wouter'
import { Button } from '../../components/Button'
import { Card } from '../../components/Card'
import { ErrorBanner } from '../../components/ErrorBanner'
import { TextInput } from '../../components/TextInput'
import { useAuth } from '../../hooks/useAuth'
import { AuthLayout } from '../../layouts/AuthLayout'
import { ROUTES } from '../../routes'
import { api } from '../../services/api'
import type { ValidateInviteResponse } from '../../services/types'
import { styled } from '../../setup'
import { colors, spacing, typography } from '../../theme'

const Content = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 420px;
`

const Logo = styled('div')`
  font-size: ${typography.fontSize3xl};
  font-weight: ${typography.fontWeightBold};
  color: ${colors.accentPrimary};
  margin-bottom: ${spacing.lg};
`

const Tagline = styled('p')`
  color: ${colors.textSecondary};
  font-size: ${typography.fontSizeLg};
  margin-bottom: ${spacing['3xl']};
  text-align: center;
`

const Form = styled('form')`
  width: 100%;
`

const LoginLink = styled('button')`
  background: none;
  border: none;
  color: ${colors.accentPrimary};
  cursor: pointer;
  font-size: ${typography.fontSizeBase};
  margin-top: ${spacing.lg};
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: ${colors.accentHover};
  }
`

// Local form state
const inviteCode = signal('')
const firstName = signal('')
const lastName = signal('')
const email = signal('')
const isSubmitting = signal(false)
const validationError = signal<string | null>(null)
const successMessage = signal<string | null>(null)

// Session storage key for validated invite code
const INVITE_CODE_KEY = 'brainrot_invite_code'
const INVITE_KIND_KEY = 'brainrot_invite_kind'

export function storeInviteCode(code: string, kind: string) {
  sessionStorage.setItem(INVITE_CODE_KEY, code)
  sessionStorage.setItem(INVITE_KIND_KEY, kind)
}

export function getStoredInviteCode(): { code: string; kind: string } | null {
  const code = sessionStorage.getItem(INVITE_CODE_KEY)
  const kind = sessionStorage.getItem(INVITE_KIND_KEY)
  if (code && kind) {
    return { code, kind }
  }
  return null
}

export function clearStoredInviteCode() {
  sessionStorage.removeItem(INVITE_CODE_KEY)
  sessionStorage.removeItem(INVITE_KIND_KEY)
}

export function LandingPage() {
  const { isAuthenticated, hasProfile, login } = useAuth()
  const [, setLocation] = useLocation()

  // If authenticated, redirect based on profile state
  if (isAuthenticated.value) {
    if (hasProfile.value) {
      setLocation(ROUTES.PROFILE)
    } else {
      setLocation(ROUTES.SIGNUP)
    }
    return null
  }

  const handleSubmit = async (e: Event) => {
    e.preventDefault()

    const code = inviteCode.value.trim().toUpperCase()
    const fName = firstName.value.trim()
    const lName = lastName.value.trim()
    const emailVal = email.value.trim()

    if (code.length < 6) {
      validationError.value = 'Invite code must be at least 6 characters'
      return
    }
    if (!fName || !lName) {
      validationError.value = 'First and last name are required'
      return
    }
    if (!emailVal || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailVal)) {
      validationError.value = 'Please enter a valid email'
      return
    }

    isSubmitting.value = true
    validationError.value = null
    successMessage.value = null

    try {
      const response = await api.get<ValidateInviteResponse>(
        `/auth/validate-invite?code=${encodeURIComponent(code)}`,
      )

      if (!response.valid || !response.kind) {
        validationError.value = response.error || 'Invalid invite code'
        return
      }

      await api.post('/auth/signup-email', {
        inviteCode: code,
        email: emailVal,
        firstName: fName,
        lastName: lName,
      })

      // Store for later profile creation post-login
      storeInviteCode(code, response.kind)
      successMessage.value = 'Check your email to verify and set your password.'
    } catch (err: any) {
      validationError.value = err.message || 'Failed to start signup'
    } finally {
      isSubmitting.value = false
    }
  }

  return (
    <AuthLayout>
      <Content>
        <Logo>Brainrot Gulch</Logo>
        <Tagline>The story you learn to craft and tell</Tagline>

        <Card title="Enter Invite Code" maxWidth="400px">
          <Form onSubmit={handleSubmit}>
            {(validationError.value || successMessage.value) && (
              <ErrorBanner
                message={validationError.value || successMessage.value || ''}
                onDismiss={() => {
                  validationError.value = null
                  successMessage.value = null
                }}
              />
            )}

            <TextInput
              label="Invite Code"
              signal={inviteCode}
              placeholder="Enter your invite code"
              autoComplete="off"
              autoFocus
            />

            <TextInput
              label="First name"
              signal={firstName}
              placeholder="Your first name"
              autoComplete="given-name"
            />

            <TextInput
              label="Last name"
              signal={lastName}
              placeholder="Your last name"
              autoComplete="family-name"
            />

            <TextInput
              label="Email"
              signal={email}
              placeholder="you@example.com"
              type="email"
              autoComplete="email"
            />

            <Button
              type="submit"
              variant="primary"
              loading={isSubmitting.value}
              disabled={isSubmitting.value}
              fullWidth
            >
              Continue
            </Button>
          </Form>

          <LoginLink onClick={() => login()}>Already have an account? Log in</LoginLink>
        </Card>
      </Content>
    </AuthLayout>
  )
}
